import createClient from 'openapi-fetch';
import type { paths } from './schema';

export type { paths, components } from './schema';

export interface ApiClientOptions {
  /** Origin of the API, e.g. http://localhost:4000 — no trailing slash, no /api suffix. */
  baseUrl: string;
}

/**
 * The types come from the API's own OpenAPI document (`pnpm contract`), so a changed
 * controller breaks the front-end build instead of failing at runtime during the event.
 */
export function createApiClient({ baseUrl }: ApiClientOptions) {
  return createClient<paths>({
    baseUrl: `${baseUrl.replace(/\/$/, '')}/api`,
    // Session cookies are how both audiences stay signed in (ACC-03, ROLE-01).
    credentials: 'include',
  });
}

export type ApiClient = ReturnType<typeof createApiClient>;

/**
 * A file, shaped so the typed client will carry it. `openapi-typescript` renders a binary form
 * field as `string`, because that is the only thing JSON Schema's `format: binary` maps to, while
 * `openapi-fetch` passes a `FormData` body through untouched and lets the browser write the
 * multipart boundary. This is the one line where those two facts have to meet, so it happens here
 * rather than at each of the five upload sites.
 */
export function fileBody(file: File): { file: string } {
  const form = new FormData();
  form.append('file', file);
  return form as unknown as { file: string };
}

/**
 * Where an uploaded file is served from. Not part of the typed client because the answer is bytes
 * rather than JSON: what a screen needs is a string to put in an `<img src>`, and the session
 * cookie rides along with the image request the same way it does with every other one.
 *
 * Null in, null out, so a screen can hand it a `photoAssetId` straight from the API and let the
 * absence of a photograph fall through to whatever it draws instead.
 */
export function createAssetUrl({ baseUrl }: ApiClientOptions) {
  const root = `${baseUrl.replace(/\/$/, '')}/api/assets`;

  return (assetId: string | null | undefined): string | null =>
    assetId ? `${root}/${assetId}` : null;
}
